import { useMemo } from 'react'
import { ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS } from '../../common/integrations'
import { type CapabilityState } from '../../models/Onboarding'
import { selectAccountId } from '../auth/authSlice'
import { useAppSelector } from '../hooks'
import { useGetMeQuery } from '../user/user'
import { useIntegrations } from '../../views/Integration/useIntegrations'
import { useContactSyncStates } from '../../views/Integration/IntegrationManagement/OnboardingSupport/useContactSyncStates'

export const useContactCapability = (): CapabilityState & { isLoading: boolean } => {
  const accountId = useAppSelector(selectAccountId)
  const { data: me, isLoading: isMeLoading } = useGetMeQuery({ accountId })
  const { isIntegrationAllowed, isIntegrationSyncDisabled, isLoading: isIntegrationsLoading } = useIntegrations()
  const { contactSyncActive, isLoading: isSyncLoading } = useContactSyncStates()

  const connectedIds = useMemo(() => new Set(me?.integrations?.map((i) => i.serviceId) ?? []), [me])

  return useMemo<CapabilityState & { isLoading: boolean }>(() => {
    const allowedServiceIds = ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.filter((id) => isIntegrationAllowed(id) && !isIntegrationSyncDisabled(id))
    const isAllowed = allowedServiceIds.length > 0
    const isFulfilled = contactSyncActive.size > 0
    const connectedIntegrations = ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.filter((id) => connectedIds.has(id))
    const connectedWithoutSync = ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.filter((id) => connectedIds.has(id) && !contactSyncActive.has(id))
    return {
      isAllowed,
      isFulfilled,
      allowedServiceIds,
      connectedIntegrations,
      connectedWithoutSync,
      isLoading: isMeLoading || isIntegrationsLoading || isSyncLoading,
    }
  }, [isIntegrationAllowed, isIntegrationSyncDisabled, connectedIds, contactSyncActive, isMeLoading, isIntegrationsLoading, isSyncLoading])
}
